import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { showSuccess } from '../components/GlobalSnackbar';
import { formatFen } from '../utils/money';

// type 与工资计算 server.js:2565-2567 对齐：advance 借支 / deduction 扣款 / subsidy 补贴
const ADJUSTMENT_TYPES = [
  { value: 'advance', label: '借支', color: 'error' },
  { value: 'deduction', label: '扣款', color: 'warning' },
  { value: 'subsidy', label: '补贴', color: 'success' },
];

const emptyForm = (today, currentMonth) => ({
  workerId: '',
  projectId: '',
  type: 'advance',
  amount: '',
  relatedMonth: currentMonth,
  date: today,
  reason: '',
  notes: '',
});

export default function AdjustmentList() {
  const [adjustments, setAdjustments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const today = new Date().toISOString().slice(0, 10);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState(emptyForm(today, currentMonth));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [adjRes, projectsRes, workersRes] = await Promise.all([
        api.get('/api/adjustments'),
        api.get('/api/projects?pageSize=500'),
        api.get('/api/workers'),
      ]);
      setAdjustments(adjRes.data);
      setProjects(projectsRes.data.data || projectsRes.data);
      setWorkers(workersRes.data);
    } catch (err) {
      console.error('Error fetching adjustments:', err);
      setError(err.response?.data?.error || err.message || '加载借支扣款记录失败');
    } finally {
      setLoading(false);
    }
  };

  const submitLock = useRef(false); // 写锁：与 QuotationDetail.writeLock 同款防连点

  const handleCreate = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const payload = {
          id: Date.now().toString(),
          ...form,
          amount: Math.round(parseFloat(form.amount || 0) * 100),
        };
        await api.post('/api/adjustments', payload);
        setOpenDialog(false);
        setForm(emptyForm(today, currentMonth));
        showSuccess('调整记录已保存');
        fetchData();
      } catch (err) {
        console.error('Error creating adjustment:', err);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleEditStart = (item) => {
    setEditingItem(item);
    setForm({
      workerId: item.workerId || '',
      projectId: item.projectId || '',
      type: item.type || 'advance',
      amount: item.amount ? String(item.amount / 100) : '',
      relatedMonth: item.relatedMonth || currentMonth,
      date: item.date || today,
      reason: item.reason || '',
      notes: item.notes || '',
    });
    setOpenDialog(true);
  };

  const handleEditSave = async () => {
    if (submitLock.current) return; // 防连点：请求在途时忽略重复提交
    submitLock.current = true;
    try {
      try {
        const data = {
          ...form,
          amount: Math.round(parseFloat(form.amount || 0) * 100),
        };
        await api.put(`/api/adjustments/${editingItem.id}`, data);
        setOpenDialog(false);
        setEditingItem(null);
        setForm(emptyForm(today, currentMonth));
        showSuccess('调整记录已更新');
        fetchData();
      } catch (err) {
        console.error('Error updating adjustment:', err);
      }
    } finally {
      submitLock.current = false;
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({ open: true, id });
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/api/adjustments/${confirmDialog.id}`);
      setConfirmDialog({ open: false, id: null });
      fetchData();
      showSuccess('调整记录已删除');
    } catch (err) {
      console.error('Error deleting adjustment:', err);
    }
  };


  const getTypeMeta = (type) =>
    ADJUSTMENT_TYPES.find((t) => t.value === type) || { label: type, color: 'default' };

  const getProjectName = (id) => projects.find((p) => p.id === id)?.name || '-';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" sx={{ mb: 3 }}>借支 / 扣款</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="outlined" onClick={fetchData}>重新加载</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">借支 / 扣款</Typography>
          <Typography variant="body2" color="text.secondary">
            记录工人借支、扣款、补贴。「归属工资月份」决定从哪个月的工资里扣除或并入。
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditingItem(null); setForm(emptyForm(today, currentMonth)); setOpenDialog(true); }}
        >
          新增
        </Button>
      </Box>

      {adjustments.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无借支 / 扣款记录
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>发生日期</TableCell>
                <TableCell>工人</TableCell>
                <TableCell>项目</TableCell>
                <TableCell>类型</TableCell>
                <TableCell align="right">金额</TableCell>
                <TableCell>归属月份</TableCell>
                <TableCell>原因</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {adjustments.map((adj) => {
                const meta = getTypeMeta(adj.type);
                return (
                  <TableRow key={adj.id}>
                    <TableCell>{adj.date}</TableCell>
                    <TableCell>{adj.workerName || '-'}</TableCell>
                    <TableCell>{getProjectName(adj.projectId)}</TableCell>
                    <TableCell><Chip size="small" color={meta.color} label={meta.label} /></TableCell>
                    <TableCell align="right">{formatFen(adj.amount)}</TableCell>
                    <TableCell>{adj.relatedMonth || '-'}</TableCell>
                    <TableCell>{adj.reason || '-'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleEditStart(adj)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(adj.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); setEditingItem(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? '编辑调整记录' : '新增借支 / 扣款'}</DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="工人"
            fullWidth
            variant="outlined"
            value={form.workerId}
            onChange={(e) => setForm({ ...form, workerId: e.target.value })}
            sx={{ mb: 2 }}
          >
            {workers.map((w) => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="类型"
            fullWidth
            variant="outlined"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            sx={{ mb: 2 }}
          >
            {ADJUSTMENT_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="金额（元）"
            fullWidth
            variant="outlined"
            type="number"
            value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="归属工资月份（决定从哪个月工资扣除）"
            fullWidth
            variant="outlined"
            type="month"
            value={form.relatedMonth}
            onChange={(e) => setForm({ ...form, relatedMonth: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="发生日期"
            fullWidth
            variant="outlined"
            type="date"
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="项目（可选）"
            fullWidth
            variant="outlined"
            value={form.projectId}
            onChange={(e) => setForm({ ...form, projectId: e.target.value })}
            sx={{ mb: 2 }}
          >
            <MenuItem value="">无</MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="原因"
            fullWidth
            variant="outlined"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="备注"
            fullWidth
            variant="outlined"
            multiline
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); setEditingItem(null); }}>取消</Button>
          <Button
            onClick={editingItem ? handleEditSave : handleCreate}
            variant="contained"
            disabled={!form.workerId || !(parseFloat(form.amount) > 0) || !form.relatedMonth}
          >
            保存
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, id: null })}
        onConfirm={handleConfirmDelete}
        title="确认删除"
        message="确定要删除这条调整记录吗？删除后该金额将不再计入对应月份的工资。"
      />
    </Box>
  );
}
